import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AbstractContentComponent } from '../../abstract-content.component';
import { Context } from '@service/context.service';
import { Column } from '@shared/table/column';
import { ChopAlterskategorie } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { ChopAlterskategorieDialogComponent } from './chop-alterskategorie-dialog/chop-alterskategorie-dialog.component';
import { ActionButtonDirective } from '@shared/table/action-button.directive';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { TableComponent } from '@shared/table/table.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChopKatalogDetailData } from './chop-katalog-detail.resolve';

@Component({
  selector: 'app-chop-katalog-detail',
  templateUrl: './chop-katalog-detail.component.html',
  styleUrls: ['./chop-katalog-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableComponent, ToolbarButtonDirective, ActionButtonDirective],
})
export class ChopKatalogDetailComponent extends AbstractContentComponent implements OnInit {
  private chopService = inject(ChopService);

  private data = this.activatedRoute.snapshot.data.detail as ChopKatalogDetailData;
  alterskategorien: ChopAlterskategorie[] = this.data.alterskategorien;
  columns: Column[] = [];
  private katalog = this.data.katalog;
  private katalogId = this.katalog.id;

  constructor() {
    super(Context.Global);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.refreshBreadcrumbs();
  }

  protected onLanguageChange(): void {
    this.setColumns();
    this.refreshBreadcrumbs();
    this.markForCheck();
  }

  reload(): void {
    this.chopService.getAlterskategorien(this.katalogId).subscribe((alterskategorien) => {
      this.alterskategorien = alterskategorien;
      this.markForCheck();
    });
  }

  showCreateDialog(): void {
    this.openDialog()?.onClose.subscribe((result) => {
      if (result) {
        this.reload();
      }
    });
  }

  showEditDialog(alterskategorie: ChopAlterskategorie): void {
    this.openDialog(alterskategorie)?.onClose.subscribe((result) => {
      if (result) {
        this.reload();
      }
    });
  }

  openDetail(alterskategorie: ChopAlterskategorie): void {
    this.navigate(['/admin-chop/alterskategorien', alterskategorie.id]);
  }

  delete(alterskategorie: ChopAlterskategorie): void {
    this.confirm({
      header: this.translate('CHOP.DELETE_ALTERSKATEGORIE_HEADER'),
      message: this.translate('CHOP.DELETE_ALTERSKATEGORIE_MESSAGE', { code: alterskategorie.code }),
      accept: () => {
        this.chopService.deleteAlterskategorie(alterskategorie.id).subscribe({
          next: () => {
            this.showSuccessMessage();
            this.reload();
          },
          error: (err) => this.showErrorMessage(undefined, err?.error?.message),
        });
      },
    });
  }

  private openDialog(alterskategorie?: ChopAlterskategorie): DynamicDialogRef | null {
    return this.dialogService.open(ChopAlterskategorieDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      width: '600px',
      header: this.translate(alterskategorie ? 'CHOP.EDIT_ALTERSKATEGORIE' : 'CHOP.CREATE_ALTERSKATEGORIE'),
      data: { katalogId: this.katalogId, alterskategorie },
    });
  }

  private refreshBreadcrumbs(): void {
    this.setBreadcrumbs([
      { label: this.translate('MENU.CHOP'), routerLink: ['/admin-chop'] },
      { label: `${this.katalog.jahr} ${this.getLocalizedText(this.katalog.bezeichnung)}` },
    ]);
  }

  private setColumns(): void {
    this.columns = [
      { field: 'code', headerKey: 'CHOP.CODE', width: '120px' },
      { field: 'bezeichnung.De', headerKey: 'GLOBAL.NAME_De', minWidth: '200px' },
      { field: 'bezeichnung.Fr', headerKey: 'GLOBAL.NAME_Fr', minWidth: '200px' },
      { field: 'bezeichnung.It', headerKey: 'GLOBAL.NAME_It', minWidth: '200px' },
      { field: 'altersVon', headerKey: 'CHOP.ALTERS_VON', width: '120px', filterType: 'integer' },
      { field: 'altersBis', headerKey: 'CHOP.ALTERS_BIS', width: '120px', filterType: 'integer' },
    ];
  }
}
